<template>
  <div class="post-job">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h3 class="mb-0">{{ editingJobId ? 'Edit Job' : 'Post a New Job' }}</h3>
            <button v-if="editingJobId" class="btn btn-sm btn-outline-secondary" @click="cancelEdit">Cancel Edit</button>
          </div>
          <div class="card-body">
            <form @submit.prevent="postJob">
              <!-- Vulnerable: Hidden field manipulation -->
              <input type="hidden" v-model="job.companyId">
              
              <div class="mb-3">
                <label for="title" class="form-label">Job Title</label>
                <!-- Vulnerable: No input validation -->
                <input 
                  type="text" 
                  class="form-control" 
                  id="title" 
                  v-model="job.title" 
                  required
                >
              </div>
              
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label for="location" class="form-label">Location</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="location" 
                      v-model="job.location"
                    >
                  </div>
                </div>
                
                <div class="col-md-6">
                  <div class="mb-3">
                    <label for="jobType" class="form-label">Job Type</label>
                    <select class="form-select" id="jobType" v-model="job.jobType">
                      <option value="full-time">Full Time</option>
                      <option value="part-time">Part Time</option>
                      <option value="contract">Contract</option>
                      <option value="internship">Internship</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label for="salaryMin" class="form-label">Minimum Salary (Rp)</label>
                    <input 
                      type="number" 
                      class="form-control" 
                      id="salaryMin" 
                      v-model="job.salaryMin"
                    >
                  </div>
                </div>
                
                <div class="col-md-6">
                  <div class="mb-3">
                    <label for="salaryMax" class="form-label">Maximum Salary (Rp)</label>
                    <input 
                      type="number" 
                      class="form-control" 
                      id="salaryMax" 
                      v-model="job.salaryMax"
                    >
                  </div>
                </div>
              </div>
              
              <div class="mb-3">
                <label for="description" class="form-label">Job Description</label>
                <!-- Vulnerable: XSS possible -->
                <textarea 
                  class="form-control" 
                  id="description" 
                  rows="5" 
                  v-model="job.description"
                  placeholder="Describe the job role, responsibilities, and what you're looking for..."
                ></textarea>
              </div>
              
              <div class="mb-3">
                <label for="requirements" class="form-label">Requirements</label>
                <!-- Vulnerable: XSS possible -->
                <textarea 
                  class="form-control" 
                  id="requirements" 
                  rows="4" 
                  v-model="job.requirements"
                  placeholder="List the required skills, experience, and qualifications..."
                ></textarea>
              </div>
              
              <div class="d-grid gap-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                  {{ editingJobId ? 'Update Job' : 'Post Job' }}
                </button>
                <button v-if="editingJobId" type="button" class="btn btn-outline-secondary" @click="cancelEdit">
                  Cancel
                </button>
              </div>
            </form>
            
            <!-- Display messages (safe output) -->
            <div v-if="error" class="alert alert-danger mt-3">{{ error }}</div>
            <div v-if="success" class="alert alert-success mt-3">{{ success }}</div>
          </div>
        </div>
        
        <!-- My Posted Jobs -->
        <div class="card mt-4">
          <div class="card-header">
            <h4>My Posted Jobs</h4>
          </div>
          <div class="card-body">
            <div v-if="myJobs.length === 0" class="text-center text-muted">
              No jobs posted yet.
            </div>
            
            <div v-else>
              <div 
                v-for="postedJob in myJobs" 
                :key="postedJob.id" 
                class="card mb-3"
              >
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-8">
                      <!-- Safe: no XSS risk -->
                      <h6>{{ postedJob.title }}</h6>
                      <p class="text-muted small">{{ postedJob.location }} • {{ postedJob.jobType }}</p>
                      <span 
                        class="badge" 
                        :class="postedJob.status === 'active' ? 'bg-success' : 'bg-secondary'"
                      >
                        {{ postedJob.status }}
                      </span>
                    </div>
                    
                    <div class="col-md-4 text-end">
                      <router-link 
                        :to="`/applications/${postedJob.id}`" 
                        class="btn btn-sm btn-outline-primary me-2"
                      >
                        View Applications
                      </router-link>
                      
                      <button 
                        class="btn btn-sm btn-outline-warning me-2" 
                        @click="editJob(postedJob)"
                      >
                        Edit
                      </button>
                      
                      <!-- Vulnerable: No authorization check -->
                      <button 
                        class="btn btn-sm btn-outline-danger" 
                        @click="deleteJob(postedJob.id)"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PostJob',
  data() {
    return {
      job: {
        companyId: null,
        title: '',
        description: '',
        requirements: '',
        location: '',
        jobType: 'full-time',
        salaryMin: null,
        salaryMax: null
      },
      editingJobId: null,
      myJobs: [],
      user: null,
      loading: false,
      error: null,
      success: null
    }
  },
  async created() {
    await this.checkAuth()
    await this.loadMyJobs()
  },
  methods: {
    async checkAuth() {
      try {
        const response = await this.$http.get('/api/auth/profile')
        if (response.data.success) {
          this.user = response.data.user
          
          if (this.user.role !== 'company') {
            this.$router.push('/jobs')
            return
          }
          
          this.job.companyId = this.user.id
        } else {
          this.$router.push('/login')
        }
      } catch (error) {
        this.$router.push('/login')
      }
    },
    
    async loadMyJobs() {
      if (!this.user) return
      
      try {
        // Vulnerable: No access control
        const response = await this.$http.get(`/api/jobs/company/${this.user.id}`)
        if (response.data.success) {
          this.myJobs = response.data.jobs || []
        }
      } catch (error) {
        console.error('Failed to load jobs:', error)
      }
    },
    
    async postJob() {
      this.loading = true
      this.error = null
      this.success = null
      
      try {
        let response
        if (this.editingJobId) {
          // Update existing job
          response = await this.$http.put(`/api/jobs/${this.editingJobId}`, this.job)
        } else {
          // Create new job
          response = await this.$http.post('/api/jobs', this.job)
        }
        
        if (response.data.success) {
          this.success = this.editingJobId ? 'Job updated successfully!' : 'Job posted successfully!'
          this.cancelEdit()
          
          // Reload jobs
          await this.loadMyJobs()
        } else {
          this.error = response.data.message
        }
      } catch (error) {
        if (error.response && error.response.data) {
          this.error = error.response.data.message
        } else {
          this.error = this.editingJobId ? 'Failed to update job.' : 'Failed to post job. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },
    
    editJob(postedJob) {
      this.editingJobId = postedJob.id
      this.job = {
        companyId: postedJob.companyId,
        title: postedJob.title,
        description: postedJob.description || '',
        requirements: postedJob.requirements || '',
        location: postedJob.location || '',
        jobType: postedJob.jobType || 'full-time',
        salaryMin: postedJob.salaryMin,
        salaryMax: postedJob.salaryMax,
        status: postedJob.status
      }
      this.error = null
      this.success = null
      // Scroll to form
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    
    cancelEdit() {
      this.editingJobId = null
      this.job = {
        companyId: this.user.id,
        title: '',
        description: '',
        requirements: '',
        location: '',
        jobType: 'full-time',
        salaryMin: null,
        salaryMax: null
      }
      this.error = null
      this.success = null
    },
    
    async deleteJob(jobId) {
      if (!confirm('Are you sure you want to delete this job?')) return
      
      try {
        // Vulnerable: No authorization check
        const response = await this.$http.delete(`/api/jobs/${jobId}`)
        
        if (response.data.success) {
          this.success = 'Job deleted successfully!'
          await this.loadMyJobs()
        } else {
          this.error = response.data.message
        }
      } catch (error) {
        this.error = 'Failed to delete job'
      }
    }
  }
}
</script>

<style scoped>
.post-job {
  margin-top: 1rem;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
</style>